import { useHistory, useLocation } from 'react-router-dom';
import {
  formatAsCurrency,
  CurrencySymbols,
  Box,
  Title,
  Card,
  AccountStatus,
  GetColorStatus,
  withStyles,
  Pagination,
  AccountBalance,
  AccountParentAccountInfo,
  useUpdateQueryParams,
} from 'vopay';
import { Button, TableFiltersContainer, TransactionStatus, Table as UITable } from 'UI';
import Table, { createTableProps, Row } from './Table/Table';

import { Fragment, FunctionComponent, JSX, useEffect, useState, useMemo } from 'react';

import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePagination } from 'vopay';

import AccountSearchForm from './AccountSearchForm';
import { useTranslation } from 'react-i18next';
import styles from './AccountsList.module.scss';
import useFetchPartnerAccounts, { PartnerAccountStatus } from '@/hooks/useApi/useFetchPartnerAccounts';

const classes = withStyles(styles);

type AccountsListProps = {
  onRouteAccess: () => void;
  exportCSV: (accountId: string | undefined) => void;
};

type QueryParams = {
  country: string;
  status: string;
  searchText: string;
  currency: string;
};

type FC = FunctionComponent<AccountsListProps>;

const AccountsList: FC = ({ onRouteAccess, exportCSV }: AccountsListProps) => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { canCreateAccount } = useModuleFeatureFlag('accounts');
  const location = useLocation();
  const [fetchPartnerAccountsResponse, refreshPartnerAccounts] = useFetchPartnerAccounts({});
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [balances, setBalances] = useState<AccountBalance[]>([]);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const status = useMemo(() => (queryParams.get('status') as PartnerAccountStatus) || '', [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  const hasSearch = useMemo(() => {
    return (
      (status !== '' && status.toLocaleLowerCase() !== 'all') ||
      (country !== '' && country.toLocaleLowerCase() !== 'all') ||
      (currency !== '' && currency.toLocaleLowerCase() !== 'all') ||
      searchText !== ''
    );
  }, [status, country, currency, searchText]);

  const filtersApplied = useMemo(() => {
    return !!country || !!status || !!currency || !!searchText;
  }, [country, status, currency, searchText]);

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(
    fetchPartnerAccountsResponse?.PaginationMetaData?.TotalRecords || 0,
    {
      pageSize: 20,
    },
  );

  useEffect(() => {
    onRouteAccess();
  }, [location]);

  useEffect(() => {
    if (fetchPartnerAccountsResponse?.success) {
      setBalances(fetchPartnerAccountsResponse.balances);
    } else {
      setBalances([]);
    }
  }, [fetchPartnerAccountsResponse]);

  useEffect(() => {
    refreshPartnerAccounts({
      Country: country || '',
      VopayAccountStatus: status || '',
      SearchText: searchText || '',
      Currency: currency || '',
      Offset: offset,
    });
  }, [country, status, searchText, currency, offset]);

  // Search is now triggered only when form is submitted, not on every state change
  // This was removed: useEffect that triggered search on searchTerm, status, country, currency changes

  function mapNestedAccounts(nestedAccounts: AccountBalance[]): Row<AccountBalance>[] {
    return (
      nestedAccounts.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: (account.SubAccounts || []).length > 0 ? mapNestedAccounts(account.SubAccounts || []) : undefined,
      })) || []
    );
  }
  function mapFlatAccounts(nestedAccounts: AccountBalance[]): Row<AccountBalance>[] {
    return (
      nestedAccounts.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: undefined,
      })) || []
    );
  }

  function getBreadcrumbs(parentAccounts: AccountParentAccountInfo[], onClick: (value: string) => void): JSX.Element {
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded(!expanded);

    // Build the tree by linking AccountID with ParentAccountID
    const buildTree = (): string[] => {
      if (parentAccounts.length === 0) return [];

      // Find the root (account with ParentAccountID === null)
      const root = parentAccounts.find((acc) => acc.ParentAccountID === null);
      if (!root) return [];

      // Build the chain from root to leaf
      const chain: string[] = [root.AccountName];
      let current = parentAccounts.find((acc) => acc.ParentAccountID === root.AccountID);

      while (current) {
        chain.push(current.AccountName);
        current = parentAccounts.find((acc) => acc.ParentAccountID === current!.AccountID);
      }

      return chain;
    };

    const tree = buildTree();
    if (tree.length === 0) return <></>;

    const renderBreadcrumbItem = (item: string, index: number) => (
      <Fragment key={item}>
        <button type="button" onClick={() => onClick(item)}>
          {item}
        </button>
        {index < tree.length - 1 && ' > '}
      </Fragment>
    );

    const renderCollapsedBreadcrumbs = () => (
      <>
        {renderBreadcrumbItem(tree[0], 0)}
        <button type="button" onClick={toggleExpanded}>
          ...
        </button>
        {'>'}
        {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
      </>
    );

    const breadcrumbs = (
      <span className={classes('breadcrumbs')}>
        {tree.length <= 2 || expanded ? tree.map(renderBreadcrumbItem) : renderCollapsedBreadcrumbs()}
      </span>
    );

    return <nav aria-label="Breadcrumbs">{breadcrumbs}</nav>;
  }

  const tableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: true,
    rows: mapNestedAccounts(balances),
    columns: [
      {
        id: 'accountName',
        header: t('Table.accountName') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) => (
          <div style={{ minWidth: '400px' }}>
            <UITable.Link
              to={{
                pathname: data.ParentAccountName
                  ? `accounts/sub-account/${data.AccountID}`
                  : `accounts/${data.AccountID}`,
                search: new URLSearchParams({
                  accountId: data.AccountID,
                  isActive: String(data.IsActive),
                  accountStatus: data.AccountStatus,
                  currency: data.Currency,
                  pending: data.PendingBalance,
                  available: data.AvailableBalance,
                  account: data.AccountBalance,
                  reserve: data.Reserve,
                  immediate: data.AvailableImmediately,
                }).toString(),
              }}
            >
              {data.AccountName}
            </UITable.Link>
            {data.DBAName && (
              <>
                <br />
                <i>{data.DBAName}</i>
              </>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        header: t('Table.status') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          data.IsActive ? (
            <TransactionStatus label={AccountStatus.Active} status="positive" />
          ) : (
            <TransactionStatus label={data.AccountStatus} status={GetColorStatus(data?.AccountStatus || '')} />
          ),
      },
      {
        id: 'country',
        header: t('Table.country') as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pending',
        header: t('Table.pending') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.PendingBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
      },
      {
        id: 'available',
        header: t('Table.available') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.AvailableBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
        hasDivider: false,
      },
    ],
  });

  const searchTableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: false,
    rows: mapFlatAccounts(balances),
    columns: [
      {
        id: 'accountName',
        header: t('Table.accountName') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) => {
          const onClick = (value: string) => {
            // When clicking breadcrumbs, update query params to search for that account
            setPageIndex(0);
            updateQueryParams({
              searchText: value,
              country: '',
              currency: '',
              status: '',
            });
          };
          return (
            <Box flx="col" ai="fs">
              {getBreadcrumbs(data.ParentAccounts, onClick)}
              <UITable.Link
                to={{
                  pathname: data.ParentAccountName
                    ? `accounts/sub-account/${data.AccountID}`
                    : `accounts/${data.AccountID}`,
                  search: new URLSearchParams({
                    accountId: data.AccountID,
                    isActive: String(data.IsActive),
                    accountStatus: data.AccountStatus,
                    currency: data.Currency,
                    pending: data.PendingBalance,
                    available: data.AvailableBalance,
                    account: data.AccountBalance,
                    reserve: data.Reserve,
                    immediate: data.AvailableImmediately,
                  }).toString(),
                }}
              >
                {data.AccountName}
              </UITable.Link>
              {data.DBAName && <i>{data.DBAName}</i>}
            </Box>
          );
        },
      },
      {
        id: 'status',
        header: t('Table.status') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          data.IsActive ? (
            <TransactionStatus label={AccountStatus.Active} status="positive" />
          ) : (
            <TransactionStatus label={data.AccountStatus} status={GetColorStatus(data?.AccountStatus || '')} />
          ),
      },
      {
        id: 'country',
        header: t('Table.country') as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pending',
        header: t('Table.pending') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.PendingBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
      },
      {
        id: 'available',
        header: t('Table.available') as string,
        align: 'left',
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.AvailableBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
      },
    ],
  });

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        <Title title={t('title')} />
        <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
          <Button
            id="accounts-ExportCSV"
            disabled={isRoleViewOnly}
            onClick={() => {
              exportCSV('');
            }}
          >
            {t('exportCSVButton')}
          </Button>
          {canCreateAccount && (
            <Button
              id="accounts-addAccount"
              colorScheme="primary"
              disabled={isRoleViewOnly}
              onClick={() => history.push('/accounts/create')}
            >
              {t('addAccountButton')}
            </Button>
          )}
        </div>
      </Box>
      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied || !!searchText}>
          <TableFiltersContainer.Trigger />
          <TableFiltersContainer.Content>
            <AccountSearchForm
              country={country}
              status={status}
              searchText={searchText}
              currency={currency}
              onAccountSearch={(values) => {
                setPageIndex(0);
                updateQueryParams({
                  country: values.Country || '',
                  status: values.VopayAccountStatus || '',
                  searchText: values.SearchText || '',
                  currency: values.Currency || '',
                });
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Box className={classes('accountsTableContainer', 'scrollbar')}>
          {!hasSearch && <Table {...tableProps} />}
          {hasSearch && <Table {...searchTableProps} />}
        </Box>
        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                setPageIndex(e.selected);
              }}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default AccountsList;
